import React, { useCallback, useState } from 'react';
import { RadioButton, TextField, ViewPane } from '@framework-controls/core';
import { strings } from 'src/localization/index';
import css from './DesktopDiagnoseModal.module.scss';
import { diagnoseResponses } from '../../DiagnoseTreeLit/DiagnoseTreeLit';
import RenderComponent from 'src/components/RenderComponent/RenderComponent';
import { ComponentMapIds } from 'src/components/map';
import DiagnoseModalFooter from './DiagnoseModalFooter';
import { ascCtaInteraction, ascFormEngagement, getElementColor } from 'src/helpers/EventHelper/EventHelper';
import { VehicleRec, ascCtaInteractionInput, ascFormEngagementInput } from 'src/models';

interface IProps {
    endDialogTree: boolean;
    otherResponse: boolean;
    title: string;
    responses: diagnoseResponses[];
    additionalComments: string;
    commentRef: React.MutableRefObject<string>;
    indexMapLength: number;
    selectedButton: string;
    error: boolean;
    vehicles: VehicleRec[];
    selectedVehicle: string | undefined;
    theme: string;
    setError: React.Dispatch<React.SetStateAction<boolean>>;
    selectButtonHandler: (value: string) => void;
    onChangeHandler: (value: string) => void;
    addHandler: (value: string) => void;
    cancelHandler: (e: any) => void;
    backHandler: (e: any) => void;
    nextHandler: (e: any) => void;
    diagnoseModalViews: any[];
}

const DesktopDiagnoseModal = (props: IProps) => {
    const [additionalComments, setAdditionalComments] = useState('');
    const views = props.diagnoseModalViews;
    const currentModalViewId = views[views.length - 1]?.modalViewId;

    const onChangeHandler = useCallback((value: string) => {
        setAdditionalComments(value);
        const notOther = document.querySelector('#h4LightBox')?.textContent;
        if (notOther !== strings.DescribeVehiclesIssue) {
            props.setError(false);
        } else if ((/\S/.test(additionalComments) && additionalComments.length > 1 && /\S/.test(value)) || /\S/.test(value)) {
            props.setError(false);
        } else {
            props.setError(true);
        }
    }, [additionalComments, props.setError]);

    const radioClickedHandler = useCallback((e: any, item: diagnoseResponses) => {
        const elementColor = getElementColor(e, 'color');
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: item.option,
            elementColor: elementColor,
            elementOrder: '',
            elementType: 'item_details',
            elementSubType: 'radio_button',
            eventAction: 'click',
            eventActionResult: 'next_step',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, e, props.vehicles, props.selectedVehicle);
    }, [props]);

    const commentsBlurHandler = useCallback(() => {
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: strings.DescribeVehiclesIssue,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp, props.vehicles, props.selectedVehicle);
    }, [props.vehicles, props.selectedVehicle]);

    // If we are at the end of the dialog tree display additional comments/add diagnosis logic.
    let diagnoseContent: JSX.Element | null = null;
    if (props.endDialogTree) {
        // If 'Other' response adjust displayed text and don't display the Final Issue box
        if (props.otherResponse) {
            diagnoseContent = (
                <div>
                    <div className={css.diagnoseOther}>
                        <h4 id='h4LightBox'>{props.title}</h4>
                        <div className={css.comments}>
                            <TextField
                                value={additionalComments}
                                id='other'
                                size='full'
                                multiline
                                type='text'
                                onChange={(_, value) => onChangeHandler(value)}
                                onBlur={commentsBlurHandler}
                                rows={5}
                                disableUnderline
                            />
                        </div>
                    </div>
                    <DiagnoseModalFooter
                        selectedButton={props.selectedButton}
                        responses={props.responses}
                        indexMapLength={props.indexMapLength}
                        cancelHandler={props.cancelHandler}
                        backHandler={props.backHandler}
                        nextHandler={props.nextHandler}
                        addHandler={props.addHandler}
                        otherResponse={props.otherResponse}
                        additionalComments={additionalComments}
                        title={props.title}
                        error={props.error}
                        theme={props.theme}
                    />
                </div>
            );
        } else {
            const renderer = () => <RenderComponent
                component={ComponentMapIds.DiagnoseEnd}
                additionalComments={props.commentRef}
                title={props.title}
                vehicles={props.vehicles}
                selectedVehicle={props.selectedVehicle}
                setError={props.setError}
            />;
            if (currentModalViewId !== 'DiagnoseEnd') {
                views.push({
                    modalViewId: ComponentMapIds.DiagnoseEnd,
                    component: ComponentMapIds.DiagnoseEnd,
                    key: ComponentMapIds.DiagnoseEnd,
                });
            }
            diagnoseContent = (
                <div>
                    <div>
                        <ViewPane
                            renderer={renderer}
                            views={views}
                            className={css.viewPane}
                        />
                        {/* {footer} */}
                        <DiagnoseModalFooter
                            selectedButton={props.selectedButton}
                            responses={props.responses}
                            indexMapLength={props.indexMapLength}
                            cancelHandler={props.cancelHandler}
                            backHandler={props.backHandler}
                            nextHandler={props.nextHandler}
                            addHandler={props.addHandler}
                            otherResponse={props.otherResponse}
                            additionalComments={props.additionalComments}
                            title={props.title}
                            error={props.error}
                            theme={props.theme}
                        />
                    </div>
                </div>
            );
        }
        // If not at the end of the dialog tree display all responses as radio buttons
    } else {
        const diagnoseTree = props.responses.map((item, index) => {
            let optionSpan = <span>{item.option}</span>;
            // Split the option string for underlining
            if (item.underlined) {
                const splitOption = item.option.split(item.underlined);
                optionSpan = (
                    <React.Fragment>
                        <span>{splitOption[0]}</span>
                        <span className={css.underlined}>{item.underlined}</span>
                        <span>{splitOption[1]}</span>
                    </React.Fragment>
                );
            }

            return (
                <div key={index} className={css.radioButton}>
                    <span onClick={(e: any) => radioClickedHandler(e, item)}>
                        <RadioButton
                            value={index.toString()}
                            color={index.toString() === props.selectedButton ? 'secondary' : 'default'}
                        />
                    </span>
                    {optionSpan}
                </div>
            );
        });
        const views = props.diagnoseModalViews;
        views.push({
            modalViewId: ComponentMapIds.DiagnoseTree,
            component: ComponentMapIds.DiagnoseTree,
            key: ComponentMapIds.DiagnoseTree,
        });
        const renderer = () => <RenderComponent
            component={ComponentMapIds.DiagnoseTree}
            title={props.title}
            selectedButton={props.selectedButton}
            diagnoseTree={diagnoseTree}
            selectButtonHandler={props.selectButtonHandler}
            key={props.title}
        />;

        diagnoseContent = <div>
            <ViewPane
                renderer={renderer}
                views={views}
                className={css.viewPane}
            />
            {/* {footer} */}
            <DiagnoseModalFooter
                selectedButton={props.selectedButton}
                responses={props.responses}
                indexMapLength={props.indexMapLength}
                cancelHandler={props.cancelHandler}
                backHandler={props.backHandler}
                nextHandler={props.nextHandler}
                addHandler={props.addHandler}
                otherResponse={props.otherResponse}
                additionalComments={props.additionalComments}
                title={props.title}
                error={props.error}
                theme={props.theme}
            />
        </div>;
    }

    return diagnoseContent;
};

export default DesktopDiagnoseModal;